import React, { FunctionComponent } from "react";
import { Pattern } from "../Pattern";
import Section from "../layout/Section";
import { TitleHeading } from "../typography/TitleHeading";
import BodyText from "../typography/BodyText";
import { useAnimation } from "../../hooks/useAnimate";
import { twMerge } from "tailwind-merge";
import { useThemed } from "../../hooks/useTheme";

type SideNumberedListProps = {
  tag: string;
  title: JSX.Element | string;
  description?: JSX.Element | string;
  items: {
    title: string;
    description: string;
  }[];
  theme?: "dark" | "white" | "gray" | "indigo";
  inverse?: boolean;
  singleColumn?: boolean;
  titleBgTag?: boolean;
  children?: React.ReactNode;
};

const SideNumberedList: FunctionComponent<SideNumberedListProps> = ({
  tag,
  title,
  titleBgTag = false,
  description,
  items,
  theme = "white",
  inverse,
  children,
}) => {
  const { id, animate } = useAnimation();
  const themed = useThemed(theme);

  return (
    <Section theme={theme} id={id}>
      <div
        className={twMerge(
          "grid grid-cols-1 gap-y-6 lg:gap-x-6 lg:grid-cols-5",
        )}
      >
        <div
          {...animate("fade-right")}
          className={twMerge(
            `z-20 max-w-3xl lg:mb-12 lg:pr-8 lg:col-span-2`,
            inverse && "order-3",
          )}
        >
          <TitleHeading
            theme={theme}
            tag={tag}
            title={title}
            bgTag={titleBgTag}
          />
          {description && (
            <BodyText
              dark={theme === "dark" || theme === "indigo"}
              className="mt-4 "
            >
              {description}
            </BodyText>
          )}
          <div className="hidden lg:block">{children}</div>
        </div>
        <ol className="order-2 col-span-3 z-10">
          {items.map((item, i) => (
            <li
              {...animate("fade-left", 50 * i)}
              key={`block${i}`}
              className="flex space-x-4 mb-8"
            >
              <span
                className={twMerge(
                  themed({
                    white: "text-gray-200",
                    gray: "text-gray-300",
                    dark: "text-gray-700",
                    indigo: "text-indigo-700",
                  }),
                  "text-9xl font-bold",
                )}
              >
                {i + 1}
              </span>
              <div>
                <h5
                  className={twMerge(
                    themed({
                      white: "text-gray-900",
                      gray: "text-gray-900",
                      dark: "text-white",
                      indigo: "text-white",
                    }),
                    "text-2xl font-bold mb-2",
                  )}
                >
                  {item.title}
                </h5>
                <p
                  className={twMerge(
                    themed({
                      white: "text-gray-700",
                      gray: "text-gray-700",
                      dark: "text-gray-300",
                      indigo: "text-gray-300",
                    }),
                    "text-justify",
                  )}
                  dangerouslySetInnerHTML={{ __html: item.description }}
                />
              </div>
            </li>
          ))}
        </ol>
        <div className="lg:hidden order-last">{children}</div>
      </div>
      <Pattern
        type="square"
        className={`hidden lg:block absolute h-24 w-1/6 top-0 right-0 ${
          theme === "dark" ? "text-gray-800" : "text-gray-200 opacity-70"
        }`}
      ></Pattern>
      <Pattern
        type="square"
        className={`hidden lg:block absolute h-1/6 xl:h-1/3 w-1/3 bottom-0 left-0 ${
          theme === "dark" ? "text-gray-800" : "text-gray-200 opacity-70"
        }`}
      ></Pattern>
    </Section>
  );
};

export default SideNumberedList;
